'use client'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useSearchParams } from 'next/navigation'
import { useSession } from 'next-auth/react'
import { t } from 'i18next'
import * as R from 'ramda'
import Button from 'app/app/(client)/components/atoms/Button/Button'
import Text from 'app/app/(client)/components/atoms/Text/Text'
import IconwithText from 'app/app/(client)/components/molecules/IconwithText/IconwithText'
import Tabs from 'app/app/(client)/components/molecules/Tabs/Tabs'
import Filters from 'app/app/(client)/components/organisms/Filters/Filters'
import SelectedBadges from 'app/app/(client)/components/organisms/SelectedBadge/SelectedBadge'
import MyOpportunities from 'app/app/(client)/components/templates/Opportunities/MyOpportunities'
import SharedOpportunities from 'app/app/(client)/components/templates/Opportunities/SharedOpportunities'
import OpportunityImportModal from 'app/app/(client)/components/templates/OpportunityImportModal/OpportunityImportModal'
import { useAppDispatch, useAppSelector } from './(client)/hooks/redux-hook'
import useDebounce from './(client)/hooks/useDebounce'
import { setOpportunityIds, setShowShares } from './(client)/redux/features/customers/opportunity-slice'
import { AppDispatch, RootState } from './(client)/redux/Store'
import { Constants } from './(client)/utils/client-constants'
import {
  deleteOpportunities,
  filterByOpportunityFilter,
  filterBySearchTerm,
  getCustomerModels,
  getCustomerOpportunityData,
  sortBySelectedOption
} from './(client)/utils/opportunity-utils'
import { capitalizeIfEnglish } from './(client)/utils/utils'
import { login } from './api/auth/auth-actions'
import { AppSession } from './api/auth/auth'
import { ClientOffer, Offer, OpportunityCustomer, Tab } from 'app/types/types'
import {
  CustomerModel,
  OpportunitiesDataType,
  OpportunitiesIndexSortBy,
  OpportunityFilterOption,
  OpportunitySortOption
} from 'app/types/ui/opportunity'
import { useNavigate } from './(client)/hooks/useNavigate'

interface CustomerPageInitData {
  dueDates: Record<string, Date>
  filteredCustomers: OpportunityCustomer[]
  initialCustomers: OpportunityCustomer[]
  offers: ClientOffer[]
  offerByOpportunityId: Record<string, Offer>
}

const Page = () => {
  const searchParams = useSearchParams()
  const query = searchParams.get('query')

  const isFromLogout = searchParams.get('login') === 'true'
  const router = useNavigate()

  const { data: session } = useSession()
  const dispatch: AppDispatch = useAppDispatch()
  const isAppInitialized = useAppSelector((state: RootState) => state.appInitialized.data)
  const { status: customerDataStatus } = useAppSelector((state: RootState) => state.customers)
  const { data: configData } = useAppSelector((state: RootState) => state.configdata)
  const { selectedForUnShare } = useAppSelector((state: RootState) => state.opportunities)
  const { data: syncStatus } = useAppSelector(state => state.currentSync)

  const [pageInitData, setPageInitData] = useState<CustomerPageInitData>({} as CustomerPageInitData)
  const [customerModels, setCustomerModels] = useState<CustomerModel[] | null>(null)
  const [opportunitiesData, setOpportunitiesData] = useState<OpportunitiesDataType>({
    myOpportunities: null,
    sharedOpportunities: null,
    status: 'idle'
  } as OpportunitiesDataType)

  const [selectedFilterOption, setSelectedFilterOption] = useState<OpportunityFilterOption[]>([])
  const [selectedSort, setSelectedSort] = useState<OpportunitySortOption>({
    key: 'sortDescending',
    value: 'Descending',
    sortType: 'dueDate' as OpportunitiesIndexSortBy,
    sortOrderDescending: true
  })
  const [tenderSearch, setTenderSearch] = useState('')
  const debouncedSearchTerm = useDebounce(tenderSearch, Constants.DEBOUNCE_DELAY)

  const { status, myOpportunities, sharedOpportunities } = opportunitiesData
  const [modalOpen, setModalOpen] = useState(false)
  const opportunitySharingActive = configData?.frontLineConfig?.opportunitySharing?.active || false
  const filterOrSearchActive = debouncedSearchTerm.length > 0 || selectedFilterOption.length > 0

  const tabOptions = useMemo<Tab[]>(() => {
    const tabs: Tab[] = [
      {
        label: capitalizeIfEnglish(t('customers.myOpportunities')),
        value: Constants.TAB_MY_OPPORTUNITIES,
        isDisabled: status !== 'succeeded',
        content: null
      }
    ]

    if (opportunitySharingActive) {
      tabs.push({
        label: capitalizeIfEnglish(t('customers.sharedOpportunities')),
        value: Constants.TAB_SHARED_OPPORTUNITIES,
        isDisabled: status !== 'succeeded',
        content: null
      })
    }

    return tabs
  }, [status, opportunitySharingActive])

  const isSharedTab = query === Constants.QUERY_SHARE
  const tabValue =
    isSharedTab && opportunitySharingActive ? Constants.TAB_SHARED_OPPORTUNITIES : Constants.TAB_MY_OPPORTUNITIES
  const currentOpportunities = tabValue === Constants.TAB_SHARED_OPPORTUNITIES ? sharedOpportunities : myOpportunities

  const isFilterDisabled = !filterOrSearchActive && (currentOpportunities || []).length === 0

  // Get opportunities data for customers
  useEffect(() => {
    const { frontLineConfig } = configData || {}
    if (!isAppInitialized || !frontLineConfig) {
      return
    }
    let isMounted = true
    const {
      opportunitySharing: { active }
    } = frontLineConfig

    if (isSharedTab && !active) {
      router.push('/')
    }
    dispatch(setShowShares(!isSharedTab || !active))

    getCustomerOpportunityData(frontLineConfig, dispatch).then(result => {
      if (isMounted && result) {
        setPageInitData(result)
      }
    })
    return () => {
      isMounted = false
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customerDataStatus, configData, dispatch, isAppInitialized, syncStatus, isSharedTab])

  // Process raw data into customer models (only when source data changes)
  useEffect(() => {
    if (R.isEmpty(pageInitData)) {
      return
    }
    const { dueDates, filteredCustomers, offerByOpportunityId } = pageInitData
    setCustomerModels(getCustomerModels(filteredCustomers, dueDates, offerByOpportunityId))
  }, [pageInitData])

  // Sort, filter, search and produce display data
  useEffect(() => {
    if (!customerModels || !configData?.frontLineConfig) {
      return
    }

    const sortedByOpportunityArr = sortBySelectedOption(customerModels, selectedSort)

    const filteredByOpportunityArr = filterByOpportunityFilter(
      sortedByOpportunityArr,
      pageInitData.offerByOpportunityId,
      configData.frontLineConfig,
      selectedFilterOption
    )

    const filteredOpportunitiesData = filterBySearchTerm(
      debouncedSearchTerm,
      filteredByOpportunityArr,
      session as AppSession,
      filterOrSearchActive
    )

    setOpportunitiesData(filteredOpportunitiesData)
  }, [
    customerModels,
    selectedSort,
    selectedFilterOption,
    debouncedSearchTerm,
    configData,
    session,
    pageInitData,
    filterOrSearchActive
  ])

  const getAllImportedOpportunityIds = useCallback((): string[] => {
    if (!sharedOpportunities) {
      return []
    }
    return sharedOpportunities.flatMap(cust => cust.opportunities.map(opp => opp.id))
  }, [sharedOpportunities])

  const allOpportunitiesAreSelected = useMemo(
    () =>
      (sharedOpportunities || []).every(customer =>
        customer.opportunities.every(opportunity => selectedForUnShare.includes(opportunity.id))
      ),
    [sharedOpportunities, selectedForUnShare]
  )

  const selectAllOpportunities = useCallback(
    (checked: boolean) => {
      dispatch(setOpportunityIds(checked ? getAllImportedOpportunityIds() : []))
    },
    [dispatch, getAllImportedOpportunityIds]
  )

  const handleOnTabChange = useCallback(
    (value: string) => {
      if (tabValue === value) {
        return
      }
      router.push(value === Constants.TAB_MY_OPPORTUNITIES ? '/' : `/?query=${Constants.QUERY_SHARE}`)
    },
    [tabValue, router]
  )

  const handleDeleteOpportunities = useCallback(() => {
    deleteOpportunities(selectedForUnShare, dispatch)
  }, [selectedForUnShare, dispatch])

  const handleOpenModal = useCallback(() => setModalOpen(true), [])

  const handleToggleSelectAll = useCallback(() => {
    selectAllOpportunities(!allOpportunitiesAreSelected)
  }, [selectAllOpportunities, allOpportunitiesAreSelected])

  const sharedOpportunitiesCount = useMemo(
    () => (sharedOpportunities || []).reduce((count, customer) => count + customer.opportunities.length, 0),
    [sharedOpportunities]
  )

  useEffect(() => {
    if (isFromLogout) {
      login()
    }
  }, [isFromLogout])

  return (
    <div className="bg-background-primary mb-1.5 lg:mb-0 px-2 py-4.5 lg:px-6 lg:py-6">
      <div className="mb-6">
        <Text id="header-opportunities-text" className="text-28px font-normal ">
          {capitalizeIfEnglish(t('customers.refresh.customerHeader'))}
        </Text>
      </div>
      <div className="flex items-center flex-col lg:flex-row order-2 lg:order-1 w-full mb-3 lg:mb-0">
        <div className="w-full lg:w-auto order-3 lg:order-1 flex-1 flex">
          <Tabs
            tabs={tabOptions}
            value={tabValue}
            onChange={handleOnTabChange}
            className="w-full px-1 lg:px-2 text-16px sm:text-18px bg-transparent"
          />
        </div>

        <div
          className={`w-full flex flex-col lg:flex-row order-1 gap-3 lg:order-2 lg:w-auto ${
            selectedFilterOption ? '' : 'mb-4 lg:mb-0'
          }  `}
        >
          <div className="order-2 lg:order-1 px-2 lg:px-0 ">
            {tabValue === Constants.TAB_SHARED_OPPORTUNITIES &&
              sharedOpportunities &&
              sharedOpportunities.length > 0 && (
                <div className="flex gap-3 w-full">
                  <Button
                    id={`select-deselect-all-button`}
                    onClick={handleToggleSelectAll}
                    variant="tertiary"
                    size="small"
                    className="btn h-9 flex items-center text-center justify-center order-1 text-[16px] font-semibold leading-6 w-1/3 lg:min-w-31.25"
                  >
                    <Text id="customers-selectAll" className="px-1.5 leading-6">
                      {allOpportunitiesAreSelected
                        ? capitalizeIfEnglish(t('customers.refresh.deselectAll'))
                        : capitalizeIfEnglish(t('customers.refresh.selectAll'))}
                    </Text>
                  </Button>

                  <Button
                    id={`delete-button`}
                    onClick={handleDeleteOpportunities}
                    variant="tertiary"
                    size="small"
                    className="btn h-9 flex items-center justify-center order-1 w-1/3"
                    disabled={selectedForUnShare.length === 0}
                  >
                    <IconwithText
                      id={`delete-button-iconwithtext`}
                      iconName={`icon-delete text-18px py-[2px]`}
                      textContent={[{ text: capitalizeIfEnglish(t('customers.refresh.delete')) }]}
                    />
                  </Button>

                  <Button
                    id={`import-button`}
                    onClick={handleOpenModal}
                    variant="tertiary"
                    size="small"
                    className="btn h-9 flex items-center justify-center order-1 w-1/3"
                  >
                    <IconwithText
                      id={`import-button-iconwithtext`}
                      iconName={`icon-import text-18px py-[2px]`}
                      textContent={[{ text: capitalizeIfEnglish(t('customers.import')) }]}
                    />
                  </Button>
                </div>
              )}
          </div>
          <div className="order-1 lg:order-2">
            <Filters
              sortKey={selectedSort}
              setSortKey={setSelectedSort}
              selectedFilterOption={selectedFilterOption}
              setSelectedFilterOption={setSelectedFilterOption}
              tenderSearch={tenderSearch}
              setTenderSearch={setTenderSearch}
              isDisabled={isFilterDisabled}
            />
          </div>
        </div>
        <div className="w-full order-2 mb-3 flex lg:hidden">
          <SelectedBadges
            id={`selected-badges-mobile`}
            filterKey={selectedFilterOption}
            setFilterKey={setSelectedFilterOption}
          />
        </div>
      </div>
      <div className={`hidden lg:flex ${selectedFilterOption?.length > 0 ? 'mt-6' : ''}`}>
        <SelectedBadges
          id={`selected-badges-desktop`}
          filterKey={selectedFilterOption}
          setFilterKey={setSelectedFilterOption}
        />
      </div>
      <div className="w-full px-1">
        {tabValue === Constants.TAB_MY_OPPORTUNITIES ? (
          <MyOpportunities customers={myOpportunities} dataStatus={status} />
        ) : (
          <SharedOpportunities
            customers={sharedOpportunities}
            dataStatus={status}
            onOpportunityDialogUpdate={setModalOpen}
          />
        )}
      </div>

      <OpportunityImportModal
        modalOpen={modalOpen}
        setModalOpen={setModalOpen}
        sharedOpportunitiesCount={sharedOpportunitiesCount}
      />
    </div>
  )
}

export default Page
